import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Users, Plus, Trash2, Loader2, ShieldCheck,
  User, RefreshCw, CheckCircle2, AlertCircle, Tag, KeyRound, History,
  X, Pencil, FolderOpen, CloudUpload, Search
} from 'lucide-react';
import { toast } from 'sonner';
import UserHistoryModal from '../components/UserHistoryModal';
import { agruparTools, filtrarTools } from '../lib/adminToolGroups';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_LABELS = {
  admin:  { label: 'Admin',  color: 'bg-[#00a1df]/20 text-[#00a1df] border-[#00a1df]/30' },
  user:   { label: 'User',   color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  loader: { label: 'Carga Docs', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' }
};

// ── Modal Reset Contraseña ───────────────────────────────────────────────────
const ResetPasswordModal = ({ user, token, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (password.length < 6) { toast.warning('La contraseña debe tener al menos 6 caracteres'); return; }
    if (password !== confirm) { toast.warning('Las contraseñas no coinciden'); return; }
    setSaving(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users/${user.id}/password`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Error al resetear contraseña');
      toast.success(`Contraseña de "${user.name}" actualizada`);
      onClose();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-[#1A1D27] border border-gray-700 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#232733]">
          <h3 className="text-lg font-bold text-white flex items-center gap-3">
            <KeyRound className="w-5 h-5 text-[#00a1df]" /> Resetear Contraseña
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4 text-sm text-gray-300">
          <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00a1df] to-[#006fa0] flex items-center justify-center text-white font-black text-sm shrink-0">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-bold text-white text-sm leading-none">{user.name}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">{user.email}</p>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase text-gray-500">Nueva contraseña</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-[#00a1df] transition-colors" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase text-gray-500">Confirmar contraseña</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              placeholder="Repetí la contraseña"
              className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-[#00a1df] transition-colors"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
          </div>
          {confirm && password !== confirm && (
            <p className="text-[11px] text-red-400 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> Las contraseñas no coinciden
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-800 bg-[#232733] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving || !password || password !== confirm}
            className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-[#00a1df] hover:bg-[#0091ca] rounded-lg shadow-lg transition-all disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Actualizar contraseña'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal Gestionar Áreas ────────────────────────────────────────────────────
const ManageAreasModal = ({ token, onClose, onAreasChanged }) => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const editInputRef = useRef(null);

  const fetchAreas = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/areas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Error al cargar áreas');
      setAreas(await resp.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAreas(); }, [fetchAreas]);

  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  const handleCreate = async () => {
    const name = newName.trim().toLowerCase();
    if (!name) return;
    setCreating(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/areas`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Error al crear área');
      toast.success(`Área "${data.name}" creada`);
      setNewName('');
      await fetchAreas();
      onAreasChanged();
    } catch (err) { toast.error(err.message); }
    finally { setCreating(false); }
  };

  const handleRename = async (id) => {
    const name = editingName.trim().toLowerCase();
    if (!name) { setEditingId(null); return; }
    setSavingId(id);
    try {
      const resp = await fetch(`${API_URL}/api/admin/areas/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Error al renombrar área');
      toast.success(`Área renombrada a "${data.name}"`);
      setEditingId(null);
      await fetchAreas();
      onAreasChanged();
    } catch (err) { toast.error(err.message); }
    finally { setSavingId(null); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const resp = await fetch(`${API_URL}/api/admin/areas/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Error al eliminar área');
      toast.success('Área eliminada');
      setConfirmDeleteId(null);
      await fetchAreas();
      onAreasChanged();
    } catch (err) { toast.error(err.message); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-[#1A1D27] border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#232733]">
          <h3 className="text-lg font-bold text-white flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-[#00a1df]" /> Gestionar Áreas
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Crear nueva área */}
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase text-gray-500">Nueva área</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                placeholder="ej: logística"
                className="flex-1 bg-[#0F1117] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00a1df] transition-colors placeholder-gray-600"
              />
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#00a1df] hover:bg-[#0091ca] rounded-lg transition-all disabled:opacity-50 shrink-0"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Crear
              </button>
            </div>
          </div>

          {/* Lista de áreas */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase text-gray-500">Áreas existentes</label>
            {loading ? (
              <div className="flex items-center justify-center py-8 text-gray-600">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : areas.length === 0 ? (
              <p className="text-sm text-gray-600 py-4 text-center">No hay áreas creadas</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {areas.map(area => (
                  <div key={area.id}
                    className="flex items-center gap-2 bg-[#0F1117] border border-gray-800 rounded-lg px-3 py-2 group">
                    {editingId === area.id ? (
                      <>
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editingName}
                          onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRename(area.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="flex-1 bg-transparent text-sm text-white focus:outline-none border-b border-[#00a1df] pb-0.5"
                        />
                        <button
                          onClick={() => handleRename(area.id)}
                          disabled={savingId === area.id}
                          className="p-1 text-green-400 hover:text-green-300 transition-colors disabled:opacity-50"
                          title="Guardar">
                          {savingId === area.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => setEditingId(null)}
                          className="p-1 text-gray-500 hover:text-gray-300 transition-colors" title="Cancelar">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : confirmDeleteId === area.id ? (
                      <>
                        <span className="flex-1 text-xs text-red-400 font-bold">¿Eliminar "{area.name}"?</span>
                        <button onClick={() => handleDelete(area.id)} disabled={deletingId === area.id}
                          className="flex items-center gap-1 px-2 py-1 text-[10px] font-black text-white bg-red-600 hover:bg-red-500 rounded transition-all disabled:opacity-50">
                          {deletingId === area.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Sí
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)}
                          className="px-2 py-1 text-[10px] font-black text-gray-400 hover:text-white bg-gray-800 rounded transition-colors">
                          No
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-gray-300 capitalize font-medium">{area.name}</span>
                        <button
                          onClick={() => { setEditingId(area.id); setEditingName(area.name); }}
                          className="p-1.5 text-gray-600 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-all opacity-0 group-hover:opacity-100"
                          title="Renombrar">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(area.id)}
                          className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-all opacity-0 group-hover:opacity-100"
                          title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-[11px] text-gray-600 leading-relaxed">
            Al renombrar un área se actualiza automáticamente en todos los usuarios asignados.
            Al eliminar un área se quita de todos los usuarios que la tenían.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-800 bg-[#232733] flex justify-end">
          <button onClick={onClose}
            className="px-5 py-2 text-sm font-bold text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Chip de herramienta con el check "permite eliminar" ──────────────────────
// Dos zonas clickeables independientes: el nombre habilita/deshabilita la
// herramienta, y el check habilita las acciones de ELIMINAR que ya existen
// dentro de ella. No se puede anidar un checkbox dentro de un <button> (HTML
// inválido y el click se propaga mal), por eso el chip es un <div> contenedor.
//
// El check arranca siempre desmarcado y queda deshabilitado si la herramienta no
// está asignada: no tiene sentido poder borrar en algo que no se ve.
// `fima` / `onToggleFima` y `cheques` / `onToggleCheques` solo llegan para
// "Carga de Documentos": dos checks más que habilitan cargar Saldo FIMA (ver
// auth.puede_cargar_fima) y los listados de cheques del banco (ver
// auth.puede_cargar_cheques). Son permisos separados a propósito: el saldo del
// fondo y la cartera de cheques los mira gente distinta.
const ToolChip = ({ tool, enabled, canDelete, onToggle, onToggleDelete, disabled, compact,
                    fima, onToggleFima, cheques, onToggleCheques }) => {
  const px = compact ? 'px-2.5 py-1' : 'px-3 py-1.5';
  const txt = compact ? 'text-[10px]' : 'text-xs';
  return (
    <div
      className={`inline-flex items-center rounded-md border transition-all ${
        enabled
          ? 'bg-[#00a1df]/20 border-[#00a1df]/40'
          : 'bg-gray-800/60 border-gray-700/60 hover:border-gray-500'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        title={enabled ? 'Quitar esta herramienta' : 'Habilitar esta herramienta'}
        className={`${px} ${txt} font-bold rounded-l-md transition-colors disabled:cursor-not-allowed ${
          enabled ? 'text-[#00a1df]' : 'text-gray-600 hover:text-gray-400'
        }`}
      >
        {tool.label}
      </button>
      <label
        title={
          !enabled
            ? 'Habilitá la herramienta para poder dar permiso de eliminar'
            : tool.deletable === false
            ? 'Esta herramienta todavía no tiene acciones de eliminar'
            : 'Permite usar las acciones de eliminar de esta herramienta'
        }
        className={`flex items-center gap-1 pl-1.5 pr-2 py-1 border-l ${
          enabled ? 'border-[#00a1df]/30' : 'border-gray-700/60'
        } ${!enabled || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <input
          type="checkbox"
          checked={!!canDelete}
          disabled={disabled || !enabled}
          onChange={onToggleDelete}
          className="w-3 h-3 accent-red-500 disabled:cursor-not-allowed"
        />
        <span
          className={`text-[9px] font-bold uppercase tracking-tight ${
            !enabled ? 'text-gray-700' : canDelete ? 'text-red-400' : 'text-gray-500'
          }`}
        >
          eliminar
        </span>
      </label>
      {onToggleFima && (
        <label
          title={
            !enabled
              ? 'Habilitá la herramienta para poder dar permiso de FIMA'
              : 'Permite cargar Saldo FIMA (captura "Inversiones en pesos")'
          }
          className={`flex items-center gap-1 pl-1.5 pr-2 py-1 border-l ${
            enabled ? 'border-[#00a1df]/30' : 'border-gray-700/60'
          } ${!enabled || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input
            type="checkbox"
            checked={!!fima}
            disabled={disabled || !enabled}
            onChange={onToggleFima}
            className="w-3 h-3 accent-purple-500 disabled:cursor-not-allowed"
          />
          <span
            className={`text-[9px] font-bold uppercase tracking-tight ${
              !enabled ? 'text-gray-700' : fima ? 'text-purple-400' : 'text-gray-500'
            }`}
          >
            fima
          </span>
        </label>
      )}
      {onToggleCheques && (
        <label
          title={
            !enabled
              ? 'Habilitá la herramienta para poder dar permiso de cheques'
              : 'Permite cargar los listados de cheques del banco (eCheqs y custodia)'
          }
          className={`flex items-center gap-1 pl-1.5 pr-2 py-1 border-l ${
            enabled ? 'border-[#00a1df]/30' : 'border-gray-700/60'
          } ${!enabled || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input
            type="checkbox"
            checked={!!cheques}
            disabled={disabled || !enabled}
            onChange={onToggleCheques}
            className="w-3 h-3 accent-emerald-500 disabled:cursor-not-allowed"
          />
          <span
            className={`text-[9px] font-bold uppercase tracking-tight ${
              !enabled ? 'text-gray-700' : cheques ? 'text-emerald-400' : 'text-gray-500'
            }`}
          >
            cheques
          </span>
        </label>
      )}
    </div>
  );
};

// Chips de herramientas agrupados por agrupador (Compras y Proveedores,
// Cobranzas y Tesorería, ...). Los grupos y su orden vienen del catálogo del
// backend; acá sólo se dibujan. Un grupo sin herramientas visibles no se
// muestra, así el filtro no deja títulos huérfanos.
//
// `onToggleGrupo(keys, activar)` habilita o quita el grupo entero de una sola
// vez: con 13 herramientas, marcarlas de a una era el trabajo más tedioso de
// esta pantalla.
const ToolPicker = ({
  tools, enabled, canDelete, onToggle, onToggleDelete, onToggleGrupo,
  disabled, compact, busqueda = '', fima, onToggleFima, cheques, onToggleCheques,
}) => {
  const visibles = useMemo(() => filtrarTools(tools, busqueda), [tools, busqueda]);
  const grupos = useMemo(() => agruparTools(visibles), [visibles]);

  if ((tools || []).length === 0) {
    return <p className="text-xs text-gray-600">No hay herramientas asignables</p>;
  }
  if (visibles.length === 0) {
    return (
      <p className="text-[10px] text-gray-600 italic">
        Ninguna herramienta coincide con la búsqueda
      </p>
    );
  }

  return (
    <div className={compact ? 'space-y-2.5' : 'space-y-3'}>
      {grupos.map((grupo) => {
        const keysGrupo = grupo.tools.map((t) => t.key);
        const todasPuestas = keysGrupo.every((k) => enabled.includes(k));
        return (
          <div key={grupo.key}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[9px] font-black uppercase tracking-[0.18em] text-gray-600 shrink-0">
                {grupo.label || 'Herramientas'}
              </span>
              {onToggleGrupo && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onToggleGrupo(keysGrupo, !todasPuestas)}
                  title={todasPuestas ? 'Quitar todas las de este grupo' : 'Habilitar todas las de este grupo'}
                  className="text-[9px] font-bold uppercase tracking-tight text-[#00a1df]/60 hover:text-[#00a1df] transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                >
                  {todasPuestas ? 'ninguna' : 'todas'}
                </button>
              )}
              <div className="h-px flex-1 bg-gray-800/80" />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {grupo.tools.map((tool) => (
                <ToolChip
                  key={tool.key}
                  tool={tool}
                  enabled={enabled.includes(tool.key)}
                  canDelete={canDelete.includes(tool.key)}
                  onToggle={() => onToggle(tool.key)}
                  onToggleDelete={() => onToggleDelete(tool.key)}
                  disabled={disabled}
                  compact={compact}
                  {...(tool.key === LOAD_DOCS_KEY && onToggleFima
                    ? { fima, onToggleFima, cheques, onToggleCheques }
                    : {})}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Herramienta que tiene el check extra "FIMA".
const LOAD_DOCS_KEY = 'load-docs';

// ── Modal Crear Usuario ──────────────────────────────────────────────────────
const CreateUserModal = ({ onClose, onCreated, token, areasDisponibles, toolsDisponibles }) => {
  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'user',
    areas: [], admin_tools: [], admin_tools_delete: [], load_docs_fima: false,
    load_docs_cheques: false,
  });
  const [saving, setSaving] = useState(false);
  const [buscaTool, setBuscaTool] = useState('');

  const toggleArea = (area) => {
    setForm(f => ({
      ...f,
      areas: f.areas.includes(area) ? f.areas.filter(a => a !== area) : [...f.areas, area]
    }));
  };

  const toggleTool = (key) => {
    setForm(f => {
      const quitando = f.admin_tools.includes(key);
      return {
        ...f,
        admin_tools: quitando ? f.admin_tools.filter(t => t !== key) : [...f.admin_tools, key],
        // Al quitar la herramienta se limpia su permiso de eliminar: nunca puede
        // quedar un permiso huérfano sobre algo que el usuario ya no ve.
        admin_tools_delete: quitando
          ? f.admin_tools_delete.filter(t => t !== key)
          : f.admin_tools_delete,
        // Mismo criterio para el check FIMA de Carga de Documentos.
        load_docs_fima: quitando && key === LOAD_DOCS_KEY ? false : f.load_docs_fima,
        load_docs_cheques: quitando && key === LOAD_DOCS_KEY ? false : f.load_docs_cheques,
      };
    });
  };

  const toggleFima = () => {
    setForm(f => (f.admin_tools.includes(LOAD_DOCS_KEY)
      ? { ...f, load_docs_fima: !f.load_docs_fima }
      : f));
  };

  const toggleCheques = () => {
    setForm(f => (f.admin_tools.includes(LOAD_DOCS_KEY)
      ? { ...f, load_docs_cheques: !f.load_docs_cheques }
      : f));
  };

  const toggleToolDelete = (key) => {
    setForm(f => ({
      ...f,
      admin_tools_delete: f.admin_tools_delete.includes(key)
        ? f.admin_tools_delete.filter(t => t !== key)
        : [...f.admin_tools_delete, key],
    }));
  };

  // Habilita o quita un grupo entero. Al quitarlo se limpian también sus
  // permisos de eliminar, igual que en el toggle de a una.
  const toggleGrupo = (keys, activar) => {
    setForm(f => ({
      ...f,
      admin_tools: activar
        ? [...new Set([...f.admin_tools, ...keys])]
        : f.admin_tools.filter(t => !keys.includes(t)),
      admin_tools_delete: activar
        ? f.admin_tools_delete
        : f.admin_tools_delete.filter(t => !keys.includes(t)),
      load_docs_fima: !activar && keys.includes(LOAD_DOCS_KEY) ? false : f.load_docs_fima,
      load_docs_cheques: !activar && keys.includes(LOAD_DOCS_KEY) ? false : f.load_docs_cheques,
    }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.warning('Nombre, email y contraseña son obligatorios'); return;
    }
    setSaving(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Error al crear usuario');
      toast.success(`Usuario "${form.name}" creado exitosamente`);
      onCreated(data);
      onClose();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-[#1A1D27] border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#232733]">
          <h3 className="text-lg font-bold text-white flex items-center gap-3">
            <Plus className="w-5 h-5 text-[#00a1df]" /> Nuevo Usuario
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4 text-sm text-gray-300">
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase text-gray-500">Nombre</label>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Juan Pérez"
              className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-[#00a1df] transition-colors" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase text-gray-500">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="juan@empresa.com"
              className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-[#00a1df] transition-colors" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase text-gray-500">Contraseña</label>
            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-[#00a1df] transition-colors" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase text-gray-500">Rol</label>
            <select value={form.role} onChange={e => setForm({
              ...form, role: e.target.value,
              // FIMA solo aplica a role "user" (el loader nunca, el admin siempre).
              load_docs_fima: e.target.value === 'user' ? form.load_docs_fima : false,
              load_docs_cheques: e.target.value === 'user' ? form.load_docs_cheques : false,
            })}
              className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2.5 text-white focus:outline-none focus:border-[#00a1df] transition-colors">
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="loader">Carga Docs (solo /load-docs)</option>
            </select>
            {form.role === 'loader' && (
              <p className="text-[10px] text-purple-400/70">
                Solo accede a la herramienta de carga de documentos. No ve quickactions ni el panel admin.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase text-gray-500">
              Áreas asignadas
              {form.role === 'admin' && <span className="ml-2 text-[#00a1df]/60">(admin ve todas)</span>}
            </label>
            <div className="flex flex-wrap gap-2">
              {areasDisponibles.map(area => (
                <button key={area.id || area.name} onClick={() => toggleArea(area.name)}
                  disabled={form.role !== 'user'}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all capitalize disabled:opacity-30 disabled:cursor-not-allowed ${
                    form.areas.includes(area.name)
                      ? 'bg-[#00a1df]/20 text-[#00a1df] border-[#00a1df]/40'
                      : 'bg-gray-800 text-gray-500 border-gray-700 hover:border-gray-500'
                  }`}>
                  {area.name}
                </button>
              ))}
              {areasDisponibles.length === 0 && (
                <p className="text-xs text-gray-600">No hay áreas creadas aún</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase text-gray-500">
              Herramientas del admin
              {form.role === 'admin' && <span className="ml-2 text-[#00a1df]/60">(admin ve todas)</span>}
            </label>
            {toolsDisponibles.length > 3 && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
                <input
                  type="search"
                  value={buscaTool}
                  onChange={e => setBuscaTool(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { setBuscaTool(''); e.currentTarget.blur(); } }}
                  placeholder="Buscar herramienta..."
                  aria-label="Buscar herramienta"
                  className="w-full bg-[#0F1117] border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#00a1df] transition-colors [&::-webkit-search-cancel-button]:appearance-none"
                />
              </div>
            )}
            <ToolPicker
              tools={toolsDisponibles}
              enabled={form.admin_tools}
              canDelete={form.admin_tools_delete}
              onToggle={toggleTool}
              onToggleDelete={toggleToolDelete}
              onToggleGrupo={toggleGrupo}
              disabled={form.role !== 'user'}
              busqueda={buscaTool}
              fima={form.load_docs_fima}
              cheques={form.load_docs_cheques}
              onToggleCheques={toggleCheques}
              onToggleFima={toggleFima}
            />
            <p className="text-[10px] text-gray-600">
              Habilita qué herramientas del panel admin verá este usuario (sin ser admin completo).
              El check <span className="text-red-400 font-bold">eliminar</span> le permite usar las
              acciones de borrado de esa herramienta. Desmarcado por defecto.
            </p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-800 bg-[#232733] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-[#00a1df] hover:bg-[#0091ca] rounded-lg shadow-lg transition-all disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {saving ? 'Creando...' : 'Crear usuario'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Fila de usuario ──────────────────────────────────────────────────────────
const UserRow = ({ user, currentUserId, token, onDeleted, onUpdated, areasDisponibles, toolsDisponibles, busquedaTools = '' }) => {
  const [role, setRole] = useState(user.role);
  const [areas, setAreas] = useState(user.areas || []);
  const [adminTools, setAdminTools] = useState(user.admin_tools || []);
  const [toolsDelete, setToolsDelete] = useState(user.admin_tools_delete || []);
  const [fima, setFima] = useState(!!user.load_docs_fima);
  const [cheques, setCheques] = useState(!!user.load_docs_cheques);
  const [savingRole, setSavingRole] = useState(false);
  const [savingAreas, setSavingAreas] = useState(false);
  const [savingTools, setSavingTools] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Sincronizar áreas si cambian desde afuera (ej: al renombrar/eliminar un área)
  useEffect(() => { setAreas(user.areas || []); }, [user.areas]);
  useEffect(() => { setAdminTools(user.admin_tools || []); }, [user.admin_tools]);
  useEffect(() => { setToolsDelete(user.admin_tools_delete || []); }, [user.admin_tools_delete]);
  useEffect(() => { setFima(!!user.load_docs_fima); }, [user.load_docs_fima]);
  useEffect(() => { setCheques(!!user.load_docs_cheques); }, [user.load_docs_cheques]);

  const isSelf = user.id === currentUserId;

  const handleRoleChange = async (newRole) => {
    setRole(newRole); setSavingRole(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users/${user.id}/role?role=${newRole}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Error al actualizar rol');
      toast.success(`Rol de ${user.name} actualizado a "${newRole}"`);
      // El backend apaga los permisos finos al salir de role "user".
      const fimaRol = newRole === 'user' ? fima : false;
      const chequesRol = newRole === 'user' ? cheques : false;
      setFima(fimaRol); setCheques(chequesRol);
      onUpdated({ ...user, role: newRole, areas, admin_tools: adminTools, admin_tools_delete: toolsDelete,
                  load_docs_fima: fimaRol, load_docs_cheques: chequesRol });
    } catch (err) { toast.error(err.message); setRole(user.role); }
    finally { setSavingRole(false); }
  };

  const toggleArea = async (areaName) => {
    const newAreas = areas.includes(areaName) ? areas.filter(a => a !== areaName) : [...areas, areaName];
    setAreas(newAreas); setSavingAreas(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users/${user.id}/areas`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ areas: newAreas })
      });
      if (!resp.ok) throw new Error('Error al actualizar áreas');
      onUpdated({ ...user, role, areas: newAreas, admin_tools: adminTools, admin_tools_delete: toolsDelete,
                  load_docs_fima: fima, load_docs_cheques: cheques });
    } catch (err) { toast.error(err.message); setAreas(areas); }
    finally { setSavingAreas(false); }
  };

  // Herramientas y permisos de borrado se guardan JUNTOS en una sola escritura:
  // así no puede quedar un permiso de eliminar sobre una herramienta recién
  // desasignada. Ante error se revierte el estado local completo.
  const guardarTools = async (newTools, newDelete, newFima = fima, newCheques = cheques) => {
    const prevTools = adminTools;
    const prevDelete = toolsDelete;
    const prevFima = fima;
    const prevCheques = cheques;
    // Sin "Carga de Documentos" no pueden quedar los checks finos.
    const fimaLimpio = !!newFima && newTools.includes(LOAD_DOCS_KEY);
    const chequesLimpio = !!newCheques && newTools.includes(LOAD_DOCS_KEY);
    setAdminTools(newTools); setToolsDelete(newDelete);
    setFima(fimaLimpio); setCheques(chequesLimpio); setSavingTools(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users/${user.id}/admin-tools`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tools: newTools, admin_tools_delete: newDelete,
                               load_docs_fima: fimaLimpio, load_docs_cheques: chequesLimpio })
      });
      if (!resp.ok) throw new Error('Error al actualizar herramientas');
      // El backend sanea la lista (intersecta contra las asignadas): se toma su
      // respuesta como verdad en vez del optimista local.
      const data = await resp.json().catch(() => ({}));
      const okTools = data.admin_tools || newTools;
      const okDelete = data.admin_tools_delete || newDelete;
      const okFima = typeof data.load_docs_fima === 'boolean' ? data.load_docs_fima : fimaLimpio;
      if (fimaLimpio && !okFima) {
        toast.warning('No se pudo habilitar FIMA para este usuario (solo aplica a usuarios con rol User y Carga de Documentos).');
      }
      const okCheques = typeof data.load_docs_cheques === 'boolean' ? data.load_docs_cheques : chequesLimpio;
      if (chequesLimpio && !okCheques) {
        toast.warning('No se pudo habilitar Cheques para este usuario (solo aplica a usuarios con rol User y Carga de Documentos).');
      }
      setAdminTools(okTools); setToolsDelete(okDelete); setFima(okFima); setCheques(okCheques);
      onUpdated({ ...user, role, areas, admin_tools: okTools, admin_tools_delete: okDelete,
                  load_docs_fima: okFima, load_docs_cheques: okCheques });
    } catch (err) {
      toast.error(err.message);
      setAdminTools(prevTools); setToolsDelete(prevDelete);
      setFima(prevFima); setCheques(prevCheques);
    }
    finally { setSavingTools(false); }
  };

  const toggleAdminTool = (key) => {
    const quitando = adminTools.includes(key);
    const newTools = quitando ? adminTools.filter(t => t !== key) : [...adminTools, key];
    const newDelete = quitando ? toolsDelete.filter(t => t !== key) : toolsDelete;
    guardarTools(newTools, newDelete);
  };

  // Todo el grupo en una sola escritura: el backend recibe la lista final, no
  // una llamada por herramienta.
  const toggleGrupo = (keys, activar) => {
    const newTools = activar
      ? [...new Set([...adminTools, ...keys])]
      : adminTools.filter(t => !keys.includes(t));
    const newDelete = activar
      ? toolsDelete
      : toolsDelete.filter(t => !keys.includes(t));
    guardarTools(newTools, newDelete);
  };

  const toggleToolDelete = (key) => {
    if (!adminTools.includes(key)) return;   // sin la herramienta no hay permiso que dar
    const newDelete = toolsDelete.includes(key)
      ? toolsDelete.filter(t => t !== key)
      : [...toolsDelete, key];
    guardarTools(adminTools, newDelete);
  };

  const toggleFima = () => {
    if (!adminTools.includes(LOAD_DOCS_KEY)) return;   // sin la herramienta no hay permiso que dar
    guardarTools(adminTools, toolsDelete, !fima);
  };

  const toggleCheques = () => {
    if (!adminTools.includes(LOAD_DOCS_KEY)) return;
    guardarTools(adminTools, toolsDelete, fima, !cheques);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Error al eliminar usuario');
      toast.success(`Usuario "${user.name}" eliminado`);
      onDeleted(user.id);
    } catch (err) { toast.error(err.message); setDeleting(false); setConfirmDelete(false); }
  };

  return (
    <tr className="border-b border-gray-800/60 hover:bg-gray-800/20 transition-colors group">
      {/* Avatar + Info */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00a1df] to-[#006fa0] flex items-center justify-center text-white font-black text-sm shadow-lg border border-[#00a1df]/20 shrink-0">
            {user.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white truncate leading-none mb-0.5">
              {user.name}
              {isSelf && <span className="ml-2 text-[9px] text-[#00a1df] font-black uppercase tracking-widest">(vos)</span>}
            </p>
            <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
          </div>
        </div>
      </td>

      {/* Rol */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-2">
          <select value={role} onChange={e => handleRoleChange(e.target.value)}
            disabled={savingRole || isSelf}
            className={`bg-[#0F1117] border rounded-lg px-2 py-1.5 text-xs font-bold focus:outline-none focus:border-[#00a1df] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              ROLE_LABELS[role]?.color || 'border-gray-700 text-gray-400'
            }`}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="loader">Carga Docs</option>
          </select>
          {savingRole && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00a1df]" />}
        </div>
      </td>

      {/* Áreas */}
      <td className="px-5 py-4">
        {role === 'admin' ? (
          <span className="text-[11px] text-[#00a1df]/70 font-bold flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Todas las áreas
          </span>
        ) : role === 'loader' ? (
          <span className="text-[11px] text-gray-600">No aplica</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {areasDisponibles.map(area => (
              <button key={area.id || area.name} onClick={() => toggleArea(area.name)}
                disabled={savingAreas}
                className={`px-2.5 py-1 rounded-md text-[10px] font-bold border transition-all capitalize disabled:opacity-60 ${
                  areas.includes(area.name)
                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/40'
                    : 'bg-gray-800/60 text-gray-600 border-gray-700/60 hover:border-gray-500 hover:text-gray-400'
                }`}>
                {area.name}
              </button>
            ))}
            {savingAreas && <Loader2 className="w-3 h-3 animate-spin text-blue-400 ml-1" />}
          </div>
        )}
      </td>

      {/* Herramientas admin */}
      <td className="px-5 py-4">
        {role === 'admin' ? (
          <span className="text-[11px] text-[#00a1df]/70 font-bold flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Todas las herramientas
          </span>
        ) : role === 'loader' ? (
          <span className="text-[11px] text-purple-400/70 font-bold flex items-center gap-1.5">
            <CloudUpload className="w-3.5 h-3.5" /> Solo /load-docs
          </span>
        ) : (
          <div className="relative">
            <ToolPicker
              tools={toolsDisponibles}
              enabled={adminTools}
              canDelete={toolsDelete}
              onToggle={toggleAdminTool}
              onToggleDelete={toggleToolDelete}
              onToggleGrupo={toggleGrupo}
              disabled={savingTools}
              busqueda={busquedaTools}
              compact
              fima={fima}
              onToggleFima={toggleFima}
              cheques={cheques}
              onToggleCheques={toggleCheques}
            />
            {savingTools && (
              <Loader2 className="w-3 h-3 animate-spin text-[#00a1df] absolute -top-1 right-0" />
            )}
          </div>
        )}
      </td>

      {/* Acciones */}
      <td className="px-5 py-4 text-right">
        <div className="flex items-center justify-end gap-1">
          {!confirmDelete && (
            <button onClick={() => setShowHistory(true)}
              className="p-2 text-gray-600 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
              title="Ver historial de actividad">
              <History className="w-4 h-4" />
            </button>
          )}
          {!confirmDelete && (
            <button onClick={() => setShowResetPassword(true)}
              className="p-2 text-gray-600 hover:text-[#00a1df] hover:bg-orange-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
              title="Resetear contraseña">
              <KeyRound className="w-4 h-4" />
            </button>
          )}
          {!isSelf && (
            confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-red-400 font-bold">¿Confirmar?</span>
                <button onClick={handleDelete} disabled={deleting}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-black text-white bg-red-600 hover:bg-red-500 rounded-lg transition-all disabled:opacity-50">
                  {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Sí, eliminar
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  className="px-2.5 py-1.5 text-[10px] font-black text-gray-400 hover:text-white bg-gray-800 rounded-lg transition-colors">
                  Cancelar
                </button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)}
                className="p-2 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                title="Eliminar usuario">
                <Trash2 className="w-4 h-4" />
              </button>
            )
          )}
        </div>

        {showResetPassword && (
          <ResetPasswordModal user={user} token={token} onClose={() => setShowResetPassword(false)} />
        )}
        {showHistory && (
          <UserHistoryModal user={user} token={token} onClose={() => setShowHistory(false)} />
        )}
      </td>
    </tr>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────
const AdminUsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areasDisponibles, setAreasDisponibles] = useState([]);
  const [toolsDisponibles, setToolsDisponibles] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Filtra los chips de herramientas de TODAS las filas a la vez (no filtra
  // usuarios: la columna que cuesta leer con 13 herramientas es esa).
  const [busquedaTools, setBusquedaTools] = useState('');
  const [showManageAreas, setShowManageAreas] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Error al cargar usuarios');
      setUsers(await resp.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [token]);

  const fetchAreas = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/areas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      setAreasDisponibles(await resp.json());
    } catch { /* silencioso */ }
  }, [token]);

  // Catálogo de herramientas admin (solo las asignables se muestran como chips)
  const fetchTools = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/tool-catalog`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const all = await resp.json();
      setToolsDisponibles(all.filter(t => t.assignable));
    } catch { /* silencioso */ }
  }, [token]);

  useEffect(() => {
    fetchUsers();
    fetchAreas();
    fetchTools();
  }, [fetchUsers, fetchAreas, fetchTools]);

  // Recargar todo cuando cambian las áreas (crear/renombrar/eliminar)
  const handleAreasChanged = useCallback(async () => {
    await fetchAreas();
    await fetchUsers(); // los usuarios pueden tener áreas actualizadas
  }, [fetchAreas, fetchUsers]);

  const handleUserCreated  = (newUser) => setUsers(prev => [...prev, newUser]);
  const handleUserDeleted  = (id)      => setUsers(prev => prev.filter(u => u.id !== id));
  const handleUserUpdated  = (updated) => setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));

  const adminCount  = users.filter(u => u.role === 'admin').length;
  const userCount   = users.filter(u => u.role === 'user').length;
  const loaderCount = users.filter(u => u.role === 'loader').length;

  return (
    <div className="text-gray-200 min-h-screen font-sans">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent flex items-center gap-3">
              <Users className="w-7 h-7 text-[#00a1df]" />
              Administrar Usuarios
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Gestioná roles, áreas y accesos del equipo
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchUsers} disabled={loading}
              className="p-2.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
              title="Recargar">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setShowManageAreas(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-black text-gray-300 bg-gray-800 hover:bg-gray-700 transition-all border border-gray-700">
              <FolderOpen className="w-4 h-4 text-[#00a1df]" />
              ÁREAS
            </button>
            <button onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-black text-white bg-[#00a1df] hover:bg-[#0091ca] transition-all border border-orange-700 shadow-lg shadow-orange-900/20">
              <Plus className="w-4 h-4" />
              NUEVO USUARIO
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total usuarios',   value: users.length, icon: Users,       color: 'text-gray-400' },
            { label: 'Administradores',  value: adminCount,   icon: ShieldCheck, color: 'text-[#00a1df]' },
            { label: 'Usuarios',         value: userCount,    icon: User,        color: 'text-blue-400' },
            { label: 'Carga Docs',       value: loaderCount,  icon: CloudUpload, color: 'text-purple-400' }
          ].map(stat => (
            <div key={stat.label} className="bg-[#1A1D27] border border-gray-800 rounded-xl p-5 flex items-center gap-4">
              <stat.icon className={`w-8 h-8 ${stat.color} opacity-60`} />
              <div>
                <p className="text-2xl font-black text-white">{loading ? '—' : stat.value}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabla */}
        <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl">
          <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex items-center justify-between">
            <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
              <Tag className="w-4 h-4 text-[#00a1df]" /> Usuarios del sistema
            </h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
                <input
                  type="search"
                  value={busquedaTools}
                  onChange={e => setBusquedaTools(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { setBusquedaTools(''); e.currentTarget.blur(); } }}
                  placeholder="Filtrar herramientas..."
                  aria-label="Filtrar herramientas de la columna Herramientas admin"
                  data-testid="filtro-herramientas-usuarios"
                  className="w-52 bg-[#0F1117] border border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#00a1df] transition-colors [&::-webkit-search-cancel-button]:appearance-none"
                />
                {busquedaTools.trim() !== '' && (
                  <button
                    type="button"
                    onClick={() => setBusquedaTools('')}
                    aria-label="Limpiar filtro"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-gray-600 hover:text-gray-200 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <span className="text-[10px] bg-orange-500/10 text-[#00a1df] px-3 py-1 rounded-full border border-[#00a1df]/20 font-black shrink-0">
                {users.length} USUARIOS
              </span>
            </div>
          </div>

          {loading ? (
            <div className="py-20 flex flex-col items-center gap-3 text-gray-600">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm">Cargando usuarios...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="py-20 flex flex-col items-center gap-3 text-gray-600">
              <AlertCircle className="w-8 h-8" />
              <p className="text-sm">No hay usuarios registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0F1117] border-b border-gray-800 text-gray-500 uppercase text-[10px] font-black tracking-widest">
                    <th className="px-5 py-3 text-left">Usuario</th>
                    <th className="px-5 py-3 text-left">Rol</th>
                    <th className="px-5 py-3 text-left">Áreas habilitadas</th>
                    <th className="px-5 py-3 text-left">Herramientas admin</th>
                    <th className="px-5 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <UserRow
                      key={user.id}
                      user={user}
                      currentUserId={currentUser?.id}
                      token={token}
                      onDeleted={handleUserDeleted}
                      onUpdated={handleUserUpdated}
                      areasDisponibles={areasDisponibles}
                      toolsDisponibles={toolsDisponibles}
                      busquedaTools={busquedaTools}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Leyenda */}
        <div className="flex items-start gap-3 p-4 bg-blue-500/5 border border-blue-500/10 rounded-xl text-xs text-blue-400/70">
          <Tag className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            <strong className="text-[#00a1df]">Admin</strong>: acceso a todas las áreas y a todo el panel, incluidas las herramientas
            exclusivas (Consumo API, Importación YIQI, Analista MongoDB, Administrar Usuarios y Tareas Programadas), que no se pueden repartir.
            <br />
            <strong className="text-blue-400">User</strong>: solo ve las Quick Actions de las áreas habilitadas y las herramientas que le asignes
            —incluida <strong className="text-purple-400">Carga de Documentos</strong>—. Si no tiene ninguna, no entra al panel.
            <br />
            <strong className="text-purple-400">Carga Docs</strong>: solo la pantalla de carga de documentos, sin panel ni Quick Actions.
            <br />
            Hacé clic en los chips para habilitar o deshabilitar áreas y herramientas directamente.
            Usá el botón <strong className="text-[#00a1df]">ÁREAS</strong> para crear, renombrar o eliminar áreas del sistema.
          </p>
        </div>

      </div>

      {showCreateModal && (
        <CreateUserModal
          token={token}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleUserCreated}
          areasDisponibles={areasDisponibles}
          toolsDisponibles={toolsDisponibles}
        />
      )}

      {showManageAreas && (
        <ManageAreasModal
          token={token}
          onClose={() => setShowManageAreas(false)}
          onAreasChanged={handleAreasChanged}
        />
      )}
    </div>
  );
};

export default AdminUsersPage;
